import { Button, Spin, Tabs } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { ReactComponent as DownloadIcon } from "../../../assets/icon/download.svg";
import PdfPreview from "../components/PdfPreview";
import { fetchFilePreviewFile } from "../api/filePreviewApi";
import {
  FilePreviewApiError,
  FilePreviewFile,
  SpreadsheetPreviewSheet
} from "../types";
import { normalizePreviewApiError } from "../utils/previewError";
import { downloadBlobFile } from "../utils/filePreview";
import styles from "./FilePreviewPage.module.scss";

const IMAGE_ZOOM_STEP = 0.25;
const MIN_IMAGE_ZOOM_RATIO = 1;
const MAX_IMAGE_ZOOM_RATIO = 3;
const IMAGE_VIEWPORT_PADDING_PX = 48;

type PreviewStateMessageProps = {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  isInline?: boolean;
};

function PreviewStateMessage({
  message,
  actionLabel = "Thử lại",
  onAction,
  isInline = false
}: PreviewStateMessageProps) {
  const className = isInline
    ? `${styles.state} ${styles.stateInline}`
    : styles.state;

  return (
    <div className={className}>
      <span>{message}</span>
      {onAction ? (
        <Button className={styles.retryButton} onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function parseFileId(fileIdParam?: string): string | null {
  if (!fileIdParam) return null;
  const normalizedFileId = fileIdParam.trim();
  return normalizedFileId || null;
}

function parseFileName(search: string): string | null {
  const fileName = new URLSearchParams(search).get("fileName");
  if (!fileName) return null;
  const normalizedFileName = fileName.trim();
  return normalizedFileName || null;
}

function isPdfPreviewCandidate(file: FilePreviewFile): boolean {
  const normalizedExtension = String(file.extension || "").trim().toLowerCase();
  const normalizedContentType = String(file.contentType || "")
    .trim()
    .toLowerCase();
  const normalizedFileName = String(file.fileName || "").trim().toLowerCase();

  return (
    file.kind === "pdf" ||
    normalizedExtension === "pdf" ||
    normalizedContentType.includes("pdf") ||
    normalizedFileName.endsWith(".pdf")
  );
}

function createSpreadsheetSheets(
  file: FilePreviewFile,
  workbook: XLSX.WorkBook
): SpreadsheetPreviewSheet[] {
  return workbook.SheetNames.map((sheetName, index) => ({
    key: `${file.fileId}-${index}-${sheetName}`,
    name: sheetName,
    html: new DOMParser().parseFromString(
      XLSX.utils.sheet_to_html(workbook.Sheets[sheetName]),
      "text/html"
    ).body.innerHTML
  }));
}

type ImageDimensions = {
  width: number;
  height: number;
};

function FilePreviewPage() {
  const { fileId: fileIdParam } = useParams();
  const location = useLocation();
  const fileId = useMemo(() => parseFileId(fileIdParam), [fileIdParam]);
  const imageViewportRef = useRef<HTMLDivElement | null>(null);
  const hintedFileName = useMemo(
    () => parseFileName(location.search),
    [location.search]
  );
  const [file, setFile] = useState<FilePreviewFile | null>(null);
  const [error, setError] = useState<FilePreviewApiError | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [isViewerLoading, setIsViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState("");
  const [textContent, setTextContent] = useState("");
  const [spreadsheetSheets, setSpreadsheetSheets] = useState<
    SpreadsheetPreviewSheet[]
  >([]);
  const [activeSheetKey, setActiveSheetKey] = useState("");
  const [pdfToolbarContainer, setPdfToolbarContainer] =
    useState<HTMLDivElement | null>(null);
  const [imageZoomRatio, setImageZoomRatio] = useState(MIN_IMAGE_ZOOM_RATIO);
  const [imageNaturalSize, setImageNaturalSize] =
    useState<ImageDimensions | null>(null);
  const [imageViewportSize, setImageViewportSize] =
    useState<ImageDimensions | null>(null);
  const [retrySeed, setRetrySeed] = useState(0);

  const activeSheet = useMemo(() => {
    return (
      spreadsheetSheets.find((sheet) => sheet.key === activeSheetKey) ||
      spreadsheetSheets[0] ||
      null
    );
  }, [activeSheetKey, spreadsheetSheets]);

  const sheetTabItems = useMemo(() => {
    return spreadsheetSheets.map((sheet) => ({
      key: sheet.key,
      label: sheet.name,
      children: null
    }));
  }, [spreadsheetSheets]);

  useEffect(() => {
    if (!fileId) return;

    let isMounted = true;
    const controller = new AbortController();

    const loadFile = async () => {
      setIsLoadingFile(true);
      setIsViewerLoading(false);
      setViewerError(null);
      setError(null);
      setFile(null);
      setTextContent("");
      setSpreadsheetSheets([]);
      setActiveSheetKey("");

      try {
        const nextFile = await fetchFilePreviewFile(
          fileId,
          controller.signal,
          hintedFileName
        );
        if (!isMounted) return;

        let nextTextContent = "";
        let nextSpreadsheetSheets: SpreadsheetPreviewSheet[] = [];

        if (nextFile.kind === "spreadsheet") {
          const arrayBuffer = await nextFile.blob.arrayBuffer();
          if (!isMounted) return;
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          nextSpreadsheetSheets = createSpreadsheetSheets(nextFile, workbook);
        } else if (nextFile.kind === "text") {
          nextTextContent = await nextFile.blob.text();
        }

        if (!isMounted) return;
        setFile(nextFile);
        setTextContent(nextTextContent);
        setSpreadsheetSheets(nextSpreadsheetSheets);
        setActiveSheetKey(nextSpreadsheetSheets[0]?.key || "");
      } catch (nextError) {
        if (!isMounted) return;
        setError(
          await normalizePreviewApiError(
            nextError,
            "Không thể tải bản xem trước."
          )
        );
      } finally {
        if (isMounted) {
          setIsLoadingFile(false);
        }
      }
    };

    loadFile();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [fileId, hintedFileName, retrySeed]);

  useEffect(() => {
    if (!file || file.kind !== "image") {
      setViewerUrl("");
      setImageZoomRatio(MIN_IMAGE_ZOOM_RATIO);
      setImageNaturalSize(null);
      setImageViewportSize(null);
      return;
    }

    const nextViewerUrl = URL.createObjectURL(file.blob);
    setViewerUrl(nextViewerUrl);
    setIsViewerLoading(true);
    setViewerError(null);

    return () => {
      URL.revokeObjectURL(nextViewerUrl);
    };
  }, [file]);

  useEffect(() => {
    if (!file || file.kind !== "image") {
      return;
    }

    const element = imageViewportRef.current;
    if (!element) {
      return;
    }

    const updateSize = () => {
      setImageViewportSize({
        width: element.clientWidth,
        height: element.clientHeight
      });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => {
        window.removeEventListener("resize", updateSize);
      };
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [file, viewerUrl]);

  useEffect(() => {
    if (
      !file ||
      ["pdf", "docx", "spreadsheet", "text", "unsupported"].includes(file.kind)
    ) {
      setIsViewerLoading(false);
      setViewerError(null);
    }
  }, [file]);

  useEffect(() => {
    const originalTitle = document.title;
    if (fileIdParam) {
      document.title = fileIdParam;
    } else if (file?.fileName) {
      document.title = file.fileName;
    } else if (hintedFileName) {
      document.title = hintedFileName;
    } else {
      document.title = "File Preview";
    }
    return () => {
      document.title = originalTitle;
    };
  }, [file?.fileName, hintedFileName, fileIdParam]);

  const imageFitScale = useMemo(() => {
    if (!imageNaturalSize || !imageViewportSize) {
      return 1;
    }

    const availableWidth = Math.max(
      imageViewportSize.width - IMAGE_VIEWPORT_PADDING_PX,
      1
    );
    const availableHeight = Math.max(
      imageViewportSize.height - IMAGE_VIEWPORT_PADDING_PX,
      1
    );

    return Math.min(
      availableWidth / imageNaturalSize.width,
      availableHeight / imageNaturalSize.height,
      1
    );
  }, [imageNaturalSize, imageViewportSize]);

  const imageZoomPercentage = useMemo(() => {
    return Math.round(imageFitScale * imageZoomRatio * 100);
  }, [imageFitScale, imageZoomRatio]);

  const imageDisplayDimensions = useMemo(() => {
    if (!imageNaturalSize) {
      return null;
    }

    const scale = imageFitScale * imageZoomRatio;

    return {
      width: imageNaturalSize.width * scale,
      height: imageNaturalSize.height * scale
    };
  }, [imageFitScale, imageNaturalSize, imageZoomRatio]);

  if (!fileId) {
    return <PreviewStateMessage message="ID tệp không hợp lệ." />;
  }

  if (isLoadingFile) {
    return (
      <div className={styles.state}>
        <Spin size="large" />
        <span>Đang tải bản xem trước...</span>
      </div>
    );
  }

  if (error) {
    return (
      <PreviewStateMessage
        message={error.message}
        onAction={
          error.canRetry ? () => setRetrySeed((prev) => prev + 1) : undefined
        }
      />
    );
  }

  if (!file) {
    return <PreviewStateMessage message="Bản xem trước hiện không khả dụng." />;
  }

  const viewerOverlayVisible = !viewerError && isViewerLoading;
  const previewTitle = file.fileName || "File Preview";
  const isPdfFile = isPdfPreviewCandidate(file);
  const isImageFile = file.kind === "image";

  const renderViewerContent = () => {
    if (viewerError) {
      return (
        <PreviewStateMessage
          message={viewerError}
          actionLabel="Tải xuống"
          onAction={() => downloadBlobFile(file.blob, file.fileName)}
          isInline
        />
      );
    }

    if (isPdfPreviewCandidate(file)) {
      return (
        <PdfPreview
          blob={file.blob}
          onDownload={() => downloadBlobFile(file.blob, file.fileName)}
          toolbarContainer={pdfToolbarContainer}
          renderToolbarInHeader
        />
      );
    }

    switch (file.kind) {
      case "docx":
        // Tạm thời tắt DOCX internal preview.
        return (
          <PreviewStateMessage
            message="Định dạng DOCX tạm thời chưa hỗ trợ xem trước"
            actionLabel="Tải xuống"
            onAction={() => downloadBlobFile(file.blob, file.fileName)}
            isInline
          />
        );

      case "spreadsheet":
        if (!activeSheet) {
          return (
            <PreviewStateMessage
              message="Không đọc được nội dung bảng tính."
              actionLabel="Tải xuống"
              onAction={() => downloadBlobFile(file.blob, file.fileName)}
              isInline
            />
          );
        }

        return (
          <>
            <div className={styles.sheetTabs}>
              <Tabs
                activeKey={activeSheet.key}
                onChange={setActiveSheetKey}
                items={sheetTabItems}
              />
            </div>
            <div className={styles.sheetViewport}>
              <div
                className={styles.sheetHtml}
                dangerouslySetInnerHTML={{ __html: activeSheet.html }}
              />
            </div>
          </>
        );

      case "image":
        if (!viewerUrl) {
          return (
            <PreviewStateMessage
              message="Bản xem trước hiện không khả dụng."
              isInline
            />
          );
        }

        return (
          <div ref={imageViewportRef} className={styles.imageViewport}>
            <div className={styles.imageCanvas}>
              <img
                src={viewerUrl}
                alt={previewTitle}
                className={styles.image}
                style={
                  imageDisplayDimensions
                    ? {
                        width: `${imageDisplayDimensions.width}px`,
                        height: `${imageDisplayDimensions.height}px`
                      }
                    : undefined
                }
                onLoad={(event) => {
                  setIsViewerLoading(false);
                  setImageZoomRatio(MIN_IMAGE_ZOOM_RATIO);
                  setImageNaturalSize({
                    width: event.currentTarget.naturalWidth || event.currentTarget.width,
                    height:
                      event.currentTarget.naturalHeight || event.currentTarget.height
                  });
                }}
                onError={() => {
                  setIsViewerLoading(false);
                  setViewerError("Không thể hiển thị bản xem trước.");
                }}
              />
            </div>
          </div>
        );

      case "text":
        return (
          <div className={styles.textViewport}>
            <pre className={styles.textContent}>{textContent}</pre>
          </div>
        );

      default:
        return (
          <PreviewStateMessage
            message="Định dạng file này hiện chưa hỗ trợ xem trước."
            actionLabel="Tải xuống"
            onAction={() => downloadBlobFile(file.blob, file.fileName)}
            isInline
          />
        );
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.title}>{previewTitle}</div>
        </div>
        {isPdfFile ? (
          <div className={styles.headerCenter}>
            <div
              ref={setPdfToolbarContainer}
              className={styles.pdfToolbarMount}
            />
          </div>
        ) : isImageFile ? (
          <div className={styles.headerCenter}>
            <div className={styles.imageToolbar}>
              <span className={styles.imageZoomValue}>
                {imageZoomPercentage}%
              </span>
              <Button
                size="small"
                onClick={() => setImageZoomRatio(MIN_IMAGE_ZOOM_RATIO)}
                disabled={imageZoomRatio <= MIN_IMAGE_ZOOM_RATIO}
              >
                Vừa khung
              </Button>
              <Button
                size="small"
                onClick={() =>
                  setImageZoomRatio((currentZoom) =>
                    Math.min(currentZoom + IMAGE_ZOOM_STEP, MAX_IMAGE_ZOOM_RATIO)
                  )
                }
                disabled={imageZoomRatio >= MAX_IMAGE_ZOOM_RATIO}
              >
                Phóng to
              </Button>
            </div>
          </div>
        ) : null}
        <div className={styles.headerActions}>
          <Button
            className={styles.downloadButton}
            onClick={() => downloadBlobFile(file.blob, file.fileName)}
            aria-label="Tải xuống"
            title="Tải xuống"
          >
            <DownloadIcon className={styles.downloadButtonIcon} />
          </Button>
        </div>
      </div>
      <div className={styles.viewer}>
        {viewerOverlayVisible && (
          <div className={styles.overlay}>
            <Spin size="large" />
          </div>
        )}
        {renderViewerContent()}
      </div>
    </div>
  );
}

export default FilePreviewPage;
